import { router } from '@inertiajs/react'
import { createConsumer } from '@rails/actioncable'
import { useEffect, useRef, useState } from 'react'
import { playChime, unlockAudio } from '../lib/sound'

interface NotificationItem {
  id: number
  title: string
  body: string | null
  url: string | null
  created_at: string
  read: boolean
}

interface Props {
  account: { id: number; name: string; slug: string }
  initialUnreadCount: number
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'ahora'
  if (mins < 60) return `hace ${mins} min`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `hace ${hours} h`
  return new Date(iso).toLocaleDateString('es-HN')
}

/** Token CSRF del meta tag — los fetch manuales NO lo incluyen por defecto
 *  (a diferencia de router.post de Inertia) y Rails responde 422 sin él. */
function csrfToken(): string {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
}

export default function NotificationBell({ account, initialUnreadCount }: Props) {
  const [count, setCount] = useState(initialUnreadCount)
  const [open, setOpen] = useState(false)
  const [notifications, setNotifications] = useState<NotificationItem[]>([])
  const [loading, setLoading] = useState(false)
  const [toast, setToast] = useState<NotificationItem | null>(null)
  const toastTimer = useRef<number | null>(null)

  // Desbloquear audio en el primer gesto del usuario (política de autoplay)
  useEffect(() => {
    const unlock = () => unlockAudio()
    window.addEventListener('pointerdown', unlock, { once: true })
    return () => window.removeEventListener('pointerdown', unlock)
  }, [])

  // Suscripción en vivo: cualquier página, cualquier usuario logueado del tenant
  useEffect(() => {
    const consumer = createConsumer()
    const sub = consumer.subscriptions.create(
      { channel: 'NotificationsChannel' },
      {
        received(data: any) {
          if (data.type === 'notification_created' && data.notification) {
            const n: NotificationItem = {
              ...data.notification,
              read: false,
            }
            setCount((c) => c + 1)
            setNotifications((prev) => [n, ...prev])
            setToast(n)
            // Sonido global: suena en CUALQUIER página admin (no solo Comandas)
            playChime()
            if (toastTimer.current) window.clearTimeout(toastTimer.current)
            toastTimer.current = window.setTimeout(() => setToast(null), 6000)
          }
        },
      }
    )
    return () => {
      sub.unsubscribe()
      consumer.disconnect()
    }
  }, [])

  const fetchNotifications = async () => {
    if (notifications.length > 0 && !loading) return
    setLoading(true)
    try {
      const res = await fetch(`/${account.slug}/notifications`)
      if (res.ok) {
        const data = await res.json()
        setNotifications(data.notifications)
        setCount(data.unread_count)
      }
    } finally {
      setLoading(false)
    }
  }

  const openDropdown = () => {
    setOpen(!open)
    if (!open) fetchNotifications()
  }

  const markAllRead = async () => {
    try {
      const res = await fetch(`/${account.slug}/notifications/read_all`, {
        method: 'POST',
        headers: { 'X-CSRF-Token': csrfToken() },
      })
      // Si el servidor no confirmó (422 CSRF, red), NO bajar el badge local —
      // reaparecería al refrescar y eso confunde.
      if (!res.ok) return
    } catch { return }
    setCount(0)
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })))
  }

  const openNotification = async (n: NotificationItem) => {
    if (!n.read) {
      // Esperar a que el servidor registre la lectura ANTES de navegar —
      // si no, la página destino carga con el unread_count viejo (badge no se quita)
      try {
        const res = await fetch(`/${account.slug}/notifications/${n.id}/read`, {
          method: 'POST',
          headers: { 'X-CSRF-Token': csrfToken() },
        })
        if (!res.ok) return
        setNotifications((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)))
        setCount((c) => Math.max(0, c - 1))
      } catch { return }
    }
    setOpen(false)
    if (n.url) router.visit(n.url)
  }

  return (
    <>
      {/* Toast de pedido web nuevo (visible en cualquier página) */}
      {toast && (
        <button
          onClick={() => openNotification(toast)}
          className="fixed top-16 right-4 z-[100] max-w-sm w-full bg-[#202020] text-white rounded-lg shadow-xl px-4 py-3 text-left hover:bg-[#333] transition-colors"
        >
          <div className="flex items-center gap-2">
            <span className="text-lg">🔔</span>
            <div className="min-w-0">
              <p className="font-semibold text-sm truncate">{toast.title}</p>
              {toast.body && <p className="text-xs text-gray-300 truncate">{toast.body}</p>}
            </div>
          </div>
        </button>
      )}

      <div className="relative">
        <button
          onClick={openDropdown}
          className="relative p-2 rounded-full hover:bg-white/10 transition-colors"
          aria-label="Notificaciones"
        >
          <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
            />
          </svg>
          {count > 0 && (
            <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
              {count > 99 ? '99+' : count}
            </span>
          )}
        </button>

        {open && (
          <>
            {/* Clic fuera para cerrar */}
            <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
            {/* En móvil: panel ancho completo con márgenes (fixed); en desktop: dropdown anclado al botón */}
            <div className="fixed inset-x-4 top-16 z-50 bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden sm:absolute sm:inset-x-auto sm:left-auto sm:right-0 sm:top-auto sm:mt-2 sm:w-80">
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                <span className="font-semibold text-[#202020] text-sm">Notificaciones</span>
                {count > 0 && (
                  <button
                    onClick={markAllRead}
                    className="text-xs text-[#55ACEE] hover:underline font-medium"
                  >
                    Marcar todas leídas
                  </button>
                )}
              </div>
              <div className="max-h-80 overflow-y-auto">
                {loading && notifications.length === 0 && (
                  <p className="px-4 py-6 text-center text-sm text-gray-400">Cargando…</p>
                )}
                {!loading && notifications.length === 0 && (
                  <p className="px-4 py-6 text-center text-sm text-gray-400">Sin notificaciones</p>
                )}
                {notifications.map((n) => (
                  <button
                    key={n.id}
                    onClick={() => openNotification(n)}
                    className={`w-full text-left px-4 py-3 border-b border-gray-50 hover:bg-gray-50 transition-colors ${
                      n.read ? 'opacity-60' : ''
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      {!n.read && <span className="mt-1.5 w-2 h-2 rounded-full bg-[#55ACEE] flex-shrink-0" />}
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-[#202020] truncate">{n.title}</p>
                        {n.body && <p className="text-xs text-gray-500 truncate">{n.body}</p>}
                        <p className="text-[10px] text-gray-400 mt-0.5">{timeAgo(n.created_at)}</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </>
  )
}
